import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  Crown,
  Gift,
  LogOut,
  Menu,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  User,
  Wallet,
  X,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { CREDIT, formatCredits } from '../../lib/format';
import { sound } from '../../lib/sound';
import { toast } from '../ui/Toast';
import { Avatar, Badge } from '../ui/primitives';
import { Button } from '../ui/Button';
import { nav, site } from '../../config/site';
import { useWallet } from '../../store/wallet';
import { useAuth, tierProgress } from '../../store/auth';
import { useSettings } from '../../store/settings';
import { track } from '../../lib/analytics';

function BalanceChip() {
  const balance = useWallet((s) => s.balance);
  const grant = useWallet((s) => s.grant);
  const hideBalances = useSettings((s) => s.hideBalances);
  const setHideBalances = useSettings((s) => s.setHideBalances);

  const claim = () => {
    sound.unlock();
    sound.play('cashout');
    grant(1000, 'Daily bonus', 'bonus');
    toast.success('Bonus claimed', `${formatCredits(1000)} added to your balance.`);
    track('deposit_simulated', { amount: 1000 });
  };

  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={() => setHideBalances(!hideBalances)}
        className="glass flex h-10 items-center gap-2 rounded-xl px-3 transition-colors hover:border-agave-400/40"
        title={hideBalances ? 'Show balance' : 'Hide balance'}
      >
        <Wallet className="size-4 text-agave-400" />
        <span className="numeric text-sm font-semibold text-white">
          {hideBalances ? `${CREDIT}••••••` : formatCredits(balance)}
        </span>
      </button>
      <Button
        variant="primary"
        size="sm"
        className="h-10 rounded-xl"
        onClick={claim}
        aria-label="Claim bonus credits"
      >
        <Plus className="size-4" />
        <span className="hidden sm:inline">Claim</span>
      </Button>
    </div>
  );
}

function ProfileMenu() {
  const [open, setOpen] = useState(false);
  const profile = useAuth((s) => s.profile);
  const logout = useAuth((s) => s.logout);
  const totalWagered = useWallet((s) => s.totalWagered);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);

  if (!profile) {
    return (
      <Link to="/login">
        <Button variant="secondary" size="sm" className="h-10 rounded-xl">
          Sign in
        </Button>
      </Link>
    );
  }

  const progress = tierProgress(totalWagered);

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="glass flex h-10 items-center gap-2 rounded-xl pl-1.5 pr-2.5 transition-colors hover:border-agave-400/40"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar seed={profile.avatarSeed} label={profile.username} size={28} />
        <span className="hidden max-w-24 truncate text-sm font-medium text-white sm:block">
          {profile.username}
        </span>
        <ChevronDown
          className={cn('size-3.5 text-ink-400 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open ? (
        <div
          role="menu"
          className="panel absolute right-0 top-12 z-50 w-64 p-2 animate-[rise_0.2s_ease-out]"
        >
          <div className="flex items-center gap-3 rounded-lg p-3">
            <Avatar seed={profile.avatarSeed} label={profile.username} size={40} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white">{profile.username}</p>
              <p className="truncate text-xs text-ink-400">{profile.email}</p>
            </div>
          </div>

          <div className="mx-3 mb-2 rounded-lg bg-white/5 p-3">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 font-semibold uppercase tracking-wide text-gold-300">
                <Crown className="size-3.5" /> {progress.tier}
              </span>
              {progress.next ? (
                <span className="text-ink-400">{progress.next}</span>
              ) : (
                <span className="text-ink-400">Max tier</span>
              )}
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-gold-500 to-gold-300"
                style={{ width: `${progress.progress * 100}%` }}
              />
            </div>
          </div>

          {[
            { label: 'Wallet', to: '/wallet', icon: Wallet },
            { label: 'Account', to: '/account', icon: User },
            { label: 'Responsible gaming', to: '/responsible-gaming', icon: ShieldCheck },
            { label: 'Settings', to: '/account#preferences', icon: Settings },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-ink-200 transition-colors hover:bg-white/6 hover:text-white"
              onClick={() => setOpen(false)}
            >
              <item.icon className="size-4" />
              {item.label}
            </Link>
          ))}

          <div className="my-1 h-px bg-white/8" />
          <button
            role="menuitem"
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-crimson-500 transition-colors hover:bg-crimson-500/10"
            onClick={() => {
              logout();
              setOpen(false);
              navigate('/');
            }}
          >
            <LogOut className="size-4" />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/casino?q=${encodeURIComponent(query)}`);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/8 bg-ink-1000/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-[1600px] items-center gap-3 px-3 sm:px-5">
        <button
          className="grid size-10 place-items-center rounded-xl text-ink-200 hover:bg-white/6 hover:text-white lg:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle navigation menu"
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
        </button>

        <Link
          to="/"
          className="flex shrink-0 items-center gap-2.5"
          aria-label={`${site.name} home`}
        >
          <span className="grid size-9 place-items-center rounded-xl bg-gradient-to-br from-agave-400 to-agave-700 shadow-[0_8px_24px_-10px_rgba(0,201,140,0.9)]">
            <svg
              viewBox="0 0 24 24"
              className="size-5 text-ink-1000"
              fill="currentColor"
              aria-hidden
            >
              <path d="M12 2c1.6 3 1.9 5.4 1 7.2 1.2-.6 2.4-1.9 3.6-3.9 1.7 3.9 2 7.2.9 9.9-1 2.4-2.9 4-5.5 4.8-2.6-.8-4.5-2.4-5.5-4.8-1.1-2.7-.8-6 .9-9.9 1.2 2 2.4 3.3 3.6 3.9-.9-1.8-.6-4.2 1-7.2Z" />
            </svg>
          </span>
          <span className="font-display text-xl font-extrabold tracking-tight text-white">
            {site.name}
          </span>
          <Badge tone="gold" className="hidden sm:inline-flex">
            18+
          </Badge>
        </Link>

        <nav className="hidden items-center gap-0.5 xl:flex" aria-label="Primary">
          {nav.primary.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-white/8 text-white'
                    : 'text-ink-300 hover:bg-white/6 hover:text-white',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden md:block">
          <label className="glass flex h-10 w-52 items-center gap-2 rounded-xl px-3 focus-within:border-agave-400/50 lg:w-64">
            <Search className="size-4 text-ink-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search games…"
              className="w-full bg-transparent text-sm text-white placeholder:text-ink-500 focus:outline-none"
              aria-label="Search games"
            />
          </label>
        </form>

        <div className="ml-auto flex items-center gap-2 md:ml-2">
          <Link
            to="/promotions"
            className="relative hidden size-10 place-items-center rounded-xl text-ink-300 transition-colors hover:bg-white/6 hover:text-white sm:grid"
            aria-label="Notifications and promotions"
          >
            <Bell className="size-5" />
            <span className="absolute right-2.5 top-2.5 size-2 rounded-full bg-gold-400 ring-2 ring-ink-1000" />
          </Link>
          <BalanceChip />
          <div className="hidden sm:block">
            <ProfileMenu />
          </div>
        </div>
      </div>

      {mobileOpen ? (
        <div className="border-t border-white/8 bg-ink-1000/95 p-3 xl:hidden">
          <form onSubmit={submitSearch} className="mb-3 md:hidden">
            <label className="glass flex h-11 items-center gap-2 rounded-xl px-3">
              <Search className="size-4 text-ink-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search games…"
                className="w-full bg-transparent text-sm text-white placeholder:text-ink-500 focus:outline-none"
                aria-label="Search games"
              />
            </label>
          </form>
          <nav className="grid grid-cols-2 gap-1.5" aria-label="Mobile">
            {nav.primary.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center justify-between rounded-xl px-3 py-3 text-sm font-medium',
                    isActive
                      ? 'bg-agave-500/15 text-white'
                      : 'bg-white/5 text-ink-200 hover:bg-white/8 hover:text-white',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/wallet"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-xl bg-white/5 px-3 py-3 text-sm font-medium text-ink-200"
            >
              <Wallet className="size-4" /> Wallet
            </Link>
            <Link
              to="/promotions"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-xl bg-white/5 px-3 py-3 text-sm font-medium text-ink-200"
            >
              <Gift className="size-4" /> Promos
            </Link>
          </nav>
          <div className="mt-3 flex justify-center sm:hidden">
            <ProfileMenu />
          </div>
        </div>
      ) : null}
    </header>
  );
}
